import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PhoneEntryStep } from "./PhoneEntryStep";
import { OtpStep } from "./OtpStep";
import { CreateAccountStep } from "./CreateAccountStep";
import { useSharedLoginOtp } from "./handler/useSharedLoginOtp";
import { useReturnContext } from "./handler/useReturnContext";
import { useProvisionAccount } from "./handler/useProvisionAccount";
import { SharedLoginPreferredLanguage } from "../../api/sharedLogin.service";
import {
  CreateAccountProfile,
  CreateAccountText,
  OtpOutcome,
  SharedLoginStep,
  VerifyOtpOutcome,
} from "./types";

export interface SharedLoginProps {
  /** Show the language toggle on the phone/OTP steps. Defaults to true. */
  showLanguageToggle?: boolean | undefined;
  /** When provided, each step renders a close (X) button beside the toggle. */
  onClose?: (() => void) | undefined;
  /**
   * Called once the account has been provisioned (sign-in complete). With the
   * create-account step on, it receives what the rider entered there.
   */
  onSuccess?: ((profile?: CreateAccountProfile) => void) | undefined;
  /** Override the phone step heading. */
  phoneTitle?: string | undefined;
  /** Override the phone step description. */
  phoneDescription?: string | undefined;
  /** Override the phone field label. */
  phoneFieldLabel?: string | undefined;
  /** Show the marketing opt-in checkbox. Defaults to true. */
  showMarketingConsent?: boolean | undefined;
  /** Override the phone step footer disclosure. */
  phoneFooterText?: React.ReactNode | undefined;
  /** Render only the card stack (no full-page wrapper) for modal embedding. */
  bare?: boolean | undefined;
  /**
   * After the code is verified, ask a new account for first name, last name and
   * an optional email. Defaults to on for the plain /shared-login page and off
   * when a `destination` is set (e.g. Flight Alerts, which only needs the phone).
   */
  showCreateAccount?: boolean | undefined;
  /** Override any string on the create-account step. */
  createAccountText?: Partial<CreateAccountText> | undefined;
  /** Show the email field on the create-account step. Defaults to true. */
  showCreateAccountEmail?: boolean | undefined;
  /** Show the receipt checkbox on the create-account step. Defaults to false. */
  showCreateAccountReceiptOptIn?: boolean | undefined;
}

const SharedLogin: React.FC<SharedLoginProps> = ({
  showLanguageToggle = true,
  onClose,
  onSuccess,
  phoneTitle,
  phoneDescription,
  phoneFieldLabel,
  showMarketingConsent = true,
  phoneFooterText,
  bare = false,
  showCreateAccount,
  createAccountText,
  showCreateAccountEmail = true,
  showCreateAccountReceiptOptIn = false,
}) => {
  const { t, i18n } = useTranslation("sharedLogin");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const destination = searchParams.get("destination");
  const askForProfile = showCreateAccount ?? !destination;

  const [step, setStep] = useState<SharedLoginStep>("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [verifiedPhoneToken, setVerifiedPhoneToken] = useState<
    string | undefined
  >(undefined);
  const [handoverErrorCode, setHandoverErrorCode] = useState<
    string | undefined
  >(undefined);
  const { sendOtp, verifyOtp, isSending, isVerifying } = useSharedLoginOtp();
  const { handOver, isWorking: isHandingOver } = useReturnContext();
  const { provisionAccount, isProvisioning } = useProvisionAccount();

  const handlePhoneSubmit = async (
    phone: string,
    consent: boolean,
  ): Promise<OtpOutcome> => {
    const result = await sendOtp(phone);
    if (result.success) {
      setPhoneNumber(phone);
      setMarketingConsent(consent);
      setStep("otp");
    }
    return result;
  };

  const handleResend = (): Promise<OtpOutcome> => sendOtp(phoneNumber);

  const handleBackToPhone = () => {
    setStep("phone");
  };

  const preferredLanguage: SharedLoginPreferredLanguage =
    i18n.language === "zh" ? "zhHK" : "en";

  // Provision the account (signing into Firebase), then hand over or finish.
  // Create-account names and email are saved with it; the host also gets them.
  const finishSignIn = async (
    token: string,
    profile?: CreateAccountProfile,
  ): Promise<OtpOutcome> => {
    const provisionResult = await provisionAccount({
      verifiedPhoneToken: token,
      ...(destination ? { signupOriginProduct: destination } : {}),
      marketingConsent,
      preferredLanguage,
      ...(profile
        ? {
            profile: {
              firstName: profile.firstName,
              lastName: profile.lastName,
              ...(profile.email ? { email: profile.email } : {}),
            },
          }
        : {}),
    });
    if (!provisionResult.success) {
      return provisionResult.errorCode !== undefined
        ? { success: false, errorCode: provisionResult.errorCode }
        : { success: false };
    }

    setVerifiedPhoneToken(token);
    setStep("verified");
    setHandoverErrorCode(undefined);
    // Account provisioned — sign-in complete. Let the host (the hailing flow)
    // flip its auth flag and close the sign-in view.
    onSuccess?.(profile);

    if (!destination) {
      return { success: true };
    }

    const handOverResult = await handOver(token, destination);
    if (!handOverResult.success || !handOverResult.returnContextToken) {
      setHandoverErrorCode(handOverResult.errorCode ?? "UNKNOWN");
      return { success: true };
    }

    navigate(destination, {
      state: { returnContextToken: handOverResult.returnContextToken },
    });
    return { success: true };
  };

  const handleVerify = async (code: string): Promise<VerifyOtpOutcome> => {
    const result = await verifyOtp(phoneNumber, code);
    if (!result.success || !result.verifiedPhoneToken) return result;

    // A phone that already has an account signs straight in; only new ones get the form.
    if (askForProfile && result.accountExists !== true) {
      // Hold the verified phone until the rider submits their details.
      setVerifiedPhoneToken(result.verifiedPhoneToken);
      setStep("createAccount");
      return result;
    }

    const finished = await finishSignIn(result.verifiedPhoneToken);
    return finished.success ? result : finished;
  };

  const handleCreateAccount = (
    profile: CreateAccountProfile,
  ): Promise<OtpOutcome> =>
    verifiedPhoneToken
      ? finishSignIn(verifiedPhoneToken, profile)
      : Promise.resolve({ success: false });

  return (
    <div
      className={
        bare
          ? "w-full flex flex-col gap-5 md:gap-6"
          : "min-h-screen bg-app flex flex-col items-center px-6 pt-[74px] pb-6 md:pt-20 md:pb-20"
      }
    >
      <div className={bare ? "w-full" : "w-full max-w-[480px] flex flex-col gap-5 md:gap-6"}>
        {step === "phone" && (
          <PhoneEntryStep
            onSubmit={handlePhoneSubmit}
            isSubmitting={isSending}
            showLanguageToggle={showLanguageToggle}
            onClose={onClose}
            title={phoneTitle}
            description={phoneDescription}
            fieldLabel={phoneFieldLabel}
            showMarketingConsent={showMarketingConsent}
            footerText={phoneFooterText}
            bare={bare}
          />
        )}
        {step === "otp" && (
          <OtpStep
            phoneNumber={phoneNumber}
            onVerify={handleVerify}
            onResend={handleResend}
            onBack={handleBackToPhone}
            isVerifying={isVerifying || isProvisioning}
            showLanguageToggle={showLanguageToggle}
            onClose={onClose}
            bare={bare}
          />
        )}
        {step === "createAccount" && (
          <CreateAccountStep
            phoneNumber={phoneNumber}
            onSubmit={handleCreateAccount}
            isSubmitting={isProvisioning}
            text={createAccountText}
            showEmail={showCreateAccountEmail}
            showReceiptOptIn={showCreateAccountReceiptOptIn}
            showLanguageToggle={showLanguageToggle}
            onClose={onClose}
            bare={bare}
          />
        )}
        {step === "verified" && verifiedPhoneToken && (
          <div className="flex flex-col gap-2 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8">
            <h1 className="font-lexend text-[24px]/[28px] font-bold tracking-[-0.8px] text-ink-100">
              {t("handover.heading")}
            </h1>
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
              {destination && isHandingOver
                ? t("handover.redirecting")
                : t("handover.subtitle")}
            </p>
            {handoverErrorCode && (
              <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
                {t("handover.errFailed")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedLogin;
